import { formatDate } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { faCircleInfo, faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { AppSettings, IState } from 'pac-api';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';
import { SubSink } from 'subsink';

import { AccountTrainingService } from '../../account-training.service';
import { Association } from '../../model/assoication';
import { TaxonomyTerm } from '../../model/taxonomy-term';
import { LicenseInfo } from '../../model/training-record';
import { TaxonomyService } from '../../taxonomy.service';
import { WizardService } from '../../wizard/wizard.service';

const now = new Date();
now.setHours(0, 0, 0, 0);

@Component({
  selector: 'app-license',
  templateUrl: './license.component.html',
  styleUrls: ['./license.component.css']
})
export class LicenseComponent implements OnInit, OnDestroy, AfterViewInit {

  public faCircleInfo = faCircleInfo;

  public faMagnifyingGlass = faMagnifyingGlass;

  @Input()
  public license: LicenseInfo;

  @Input()
  public groupName: string;

  @Input()
  public parentGroup: UntypedFormGroup;

  @Input()
  public Asn: Association;

  public group: UntypedFormGroup;

  public regulation: Observable<TaxonomyTerm>;

  public Skip: boolean = false;

  public get UrlBase(): string {
    return (this.Asn === this.settings.pac_base_asn_code) ?
      '/'
      : this.settings.systemoptions['PAC.Web.' + this.Asn];
  }

  public get isExpired(): boolean {
    return !!this.group.value.Expire && (new Date(this.group.value.Expire)) < now;
  }

  public get Exempt(): string[] {
    return this.service.Exempt.get(this.Asn);
  }

  public get isExempt(): boolean {
    if (this.group.value.State) {
      return this.Exempt.indexOf(this.group.value.State.sta_code) !== -1;
    }

    return false;
  }

  public Disabled$: Subject<boolean> = new BehaviorSubject(false);

  public HasValue$: Subject<boolean> = new BehaviorSubject(false);

  public HasRawError: Observable<boolean>;

  public HasExpireError: Observable<string|boolean>;

  public showClear: boolean = false;

  private subsink: SubSink = new SubSink();

  constructor(
    private service: AccountTrainingService,
    private taxonomy: TaxonomyService,
    public settings: AppSettings,
  ) {
  }

  public ngOnInit(): void {

    let stateValue: IState;
    let expireValue: string;

    if (this.license.State) {
      stateValue = typeof (this.license.State) === 'object' ? this.license.State : this.settings.states.find(sta => sta.sta_code === this.license.State);
      if(stateValue) {
        this.regulation = this.taxonomy.GetTaxonomyTerm('state_regulation_detail', stateValue['sta_name'], this.Asn);
      }
    }

    if (this.license.Expire) {
      const format = 'MM/dd/yyyy';
      expireValue = formatDate(this.license.Expire, format, 'en-US');
    }

    const State = new UntypedFormControl(stateValue, []);
    const Raw = new UntypedFormControl(this.license.Raw, [Validators.required]);
    const Expire = new UntypedFormControl(expireValue, [Validators.required, WizardService.FutureDate, WizardService.ValidDate]);
    const Exempt = new UntypedFormControl(this.license.Exempt, []);

    this.group = new UntypedFormGroup({ State, Raw, Expire, Exempt });
    this.parentGroup.addControl(this.groupName, this.group);

    if (!stateValue) {
      Raw.disable();
      Expire.disable();
      Exempt.disable();
    } else if (expireValue || this.license.Raw) {
      Exempt.disable();
      this.HasValue$.next(true);
    } else if (this.Skip) {
      this.Disabled$.next(true);
    } else if (this.license.Exempt || this.isExempt) {
      Raw.disable();
      Expire.disable();
    }

    this.HasRawError = Raw.statusChanges.pipe(
      map(status => status === 'INVALID'),
      startWith((this.groupName === 'License1') && !Raw.value),
      shareReplay(1),
    );

    this.HasExpireError = combineLatest([Expire.statusChanges, this.Disabled$]).pipe(
      map(([state, disabled]) => {
        if (state == 'DISABLED') {
          return false;
        }
        
        if (state == 'INVALID' && (Expire.errors['required'] || Expire.errors['ValidDate'])) {
          return 'required';
        } else if (Expire.value < now && !disabled) {
          return 'expired';
        }

        return false;
      }),
      startWith((function() {
        if (this.Skip) {
          return false;
        }

        if (!Expire.value) {
          return 'required';
        } else if (Expire.value < now) {
          return 'expired';
        }

        return false;
      }).bind(this)()),
      shareReplay(1),
    );

    if (this.groupName === 'License1') {
      State.setValidators([Validators.required]);
    } else {
      this.showClear = true;
    }

    this.subsink.sink = this.Disabled$.subscribe({
      next: disabled => {
        if (disabled) {
          Raw.setValidators([]);
          Expire.setValidators([]);
        } else {
          Raw.setValidators([Validators.required]);
          Expire.setValidators([Validators.required, WizardService.FutureDate, WizardService.ValidDate]);
        }

        Raw.updateValueAndValidity();
        Expire.updateValueAndValidity();
      }
    })
  }

  public ngAfterViewInit(): void {
    const { State, Raw, Expire, Exempt } = this.group.controls;

    this.subsink.sink = Exempt.valueChanges.subscribe(exempt => {
      this.Skip = false;

      if (exempt) {
        Raw.reset(null);
        Expire.reset(null);
        Raw.disable();
        Expire.disable();
      } else if (Exempt.enabled) {
        Raw.enable();
        Expire.enable();
        this.Disabled$.next(false);
      }
    });

    this.subsink.sink = State.valueChanges.subscribe(state => {
      if (state) {
        if (typeof state === 'string') {
          state = this.settings.states.find(sta => sta.sta_code === state);
        }

        this.regulation = this.taxonomy.GetTaxonomyTerm('state_regulation_detail', state['sta_name'], this.Asn);

        if (this.Exempt.indexOf(state.sta_code) !== -1) {
          Raw.disable();
          Expire.disable();
          Exempt.disable();
        } else {
          Raw.enable();
          Expire.enable();
          Exempt.enable();
        }
      } else {
        this.regulation = null;

        Raw.reset(null);
        Expire.reset(null);
        Exempt.reset(null);

        Raw.disable();
        Expire.disable();
        Exempt.disable();
      }
    });

    this.subsink.sink = this.group.valueChanges.subscribe({
      next: ({ Raw, Expire }) => {
        if (Raw || Expire) {
          if (Exempt.enabled) {
            Exempt.disable({ emitEvent: false });
          }
        } else if (Exempt.disabled) {
          Exempt.enable({ emitEvent: false });
        }
      }
    });
  }

  public ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  public SkipChange(event: Event): void {
    if (this.Skip || this.isExempt) {
      this.Disabled$.next(true);
    } else {
      this.Disabled$.next(false);
    }
  }

  public Clear(event: Event): void {
    event.preventDefault();

    const { State, Raw, Expire, Exempt } = this.group.controls;

    State.setValue(null);
  }
}
